import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Box, IconButton, Sheet, Typography, useColorScheme } from "@mui/joy";
import { useState } from "react";
import { hatchlingPoems } from "../../data/hatchlingData";

interface HatchlingPoemDisplayProps {
  poemId: string;
}

const HatchlingPoemDisplay = ({ poemId }: HatchlingPoemDisplayProps) => {
  const [expanded, setExpanded] = useState(true);
  const { mode } = useColorScheme();
  const isDark = mode === "dark";

  const poem = hatchlingPoems[poemId];

  if (!poem) return null;

  const toggleExpanded = () => {
    setExpanded(!expanded);
  };

  // Ember/red color scheme for Hatchling
  const emberColor = isDark ? "rgba(239, 68, 68, 0.8)" : "rgba(185, 28, 28, 0.9)";
  const emberBgLight = "rgba(254, 226, 226, 0.6)";
  const emberBgDark = "rgba(127, 29, 29, 0.15)";
  const emberBorderLight = "rgba(185, 28, 28, 0.3)";
  const emberBorderDark = "rgba(239, 68, 68, 0.25)";

  return (
    <Sheet
      variant="outlined"
      sx={{
        my: 4,
        mx: "auto",
        maxWidth: "90%",
        position: "relative",
        borderRadius: "12px",
        bgcolor: isDark ? emberBgDark : emberBgLight,
        boxShadow: "md",
        overflow: "hidden",
        transition: "all 0.3s ease",
        borderColor: isDark ? emberBorderDark : emberBorderLight,
        transform: expanded ? "none" : "scale(0.97)",
        height: expanded ? "auto" : "120px",
      }}
    >
      {/* Decorative top edge */}
      <Box
        sx={{
          height: "24px",
          bgcolor: isDark
            ? "rgba(127, 29, 29, 0.3)"
            : "rgba(185, 28, 28, 0.15)",
          borderBottom: "1px solid",
          borderColor: isDark ? emberBorderDark : emberBorderLight,
          borderTopLeftRadius: "12px",
          borderTopRightRadius: "12px",
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {/* Decorative flourish */}
        <Typography
          sx={{
            color: emberColor,
            fontSize: "1.2rem",
            opacity: 0.6,
          }}
        >
          ✦
        </Typography>
      </Box>

      {/* Poem content */}
      <Box
        sx={{
          p: { xs: 3, md: 4 },
          pt: 2,
          maxHeight: expanded ? "600px" : "80px",
          overflow: expanded ? "auto" : "hidden",
          transition: "max-height 0.5s ease",
          position: "relative",
        }}
      >
        <Typography
          level="title-lg"
          sx={{
            fontFamily: "'Cinzel', serif",
            mb: 3,
            color: emberColor,
            textAlign: "center",
            fontWeight: "bold",
            position: "relative",
            "&::after": {
              content: '""',
              display: "block",
              width: "40%",
              height: "1px",
              backgroundColor: emberColor,
              margin: "8px auto 0",
              opacity: 0.5,
            },
          }}
        >
          {poem.title}
        </Typography>

        <Typography
          level="body-md"
          sx={{
            fontFamily: "'EB Garamond', serif",
            whiteSpace: "pre-wrap",
            color: isDark ? "neutral.100" : "neutral.800",
            fontSize: { xs: "1.1rem", md: "1.2rem" },
            lineHeight: 2,
            letterSpacing: "0.02em",
            textAlign: "center",
            fontStyle: "italic",
            position: "relative",
            zIndex: 2,
          }}
        >
          {poem.content}
        </Typography>
      </Box>

      {/* Decorative bottom edge */}
      <Box
        sx={{
          height: "24px",
          bgcolor: isDark
            ? "rgba(127, 29, 29, 0.3)"
            : "rgba(185, 28, 28, 0.15)",
          borderTop: "1px solid",
          borderColor: isDark ? emberBorderDark : emberBorderLight,
          borderBottomLeftRadius: "12px",
          borderBottomRightRadius: "12px",
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {/* Decorative flourish */}
        <Typography
          sx={{
            color: emberColor,
            fontSize: "1.2rem",
            opacity: 0.6,
          }}
        >
          ✦
        </Typography>
      </Box>

      {/* Expand/Collapse button */}
      <IconButton
        onClick={toggleExpanded}
        size="sm"
        variant="outlined"
        color="neutral"
        sx={{
          position: "absolute",
          bottom: expanded ? 30 : 10,
          right: 10,
          backgroundColor: isDark
            ? "rgba(0, 0, 0, 0.4)"
            : "rgba(255, 255, 255, 0.7)",
          border: "1px solid",
          borderColor: isDark
            ? "rgba(255, 255, 255, 0.2)"
            : "rgba(0, 0, 0, 0.1)",
          "&:hover": {
            backgroundColor: isDark
              ? "rgba(0, 0, 0, 0.5)"
              : "rgba(255, 255, 255, 0.9)",
          },
        }}
      >
        {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
      </IconButton>
    </Sheet>
  );
};

export default HatchlingPoemDisplay;
